import Client from '../../../model/api/Client';
import React, { useCallback, useEffect, useState } from 'react';
import validator from 'validator';
import AppCookies from '../../../model/cookies/AppCookies';

interface IProps {
	modal: any;
	apiClient: Client;
	codexUrl: string;
	termsUrl: string;
	gdprUrl: string;
	defaultEmail?: string;
	defaultName?: string;
}

interface IErrors {
	global: string;
	name: string;
	email: string;
	reason: string;
}

interface IValues {
	interviewId: number | null;
	name: string;
	email: string;
	reason: string;
}

export const Form = ({
	modal,
	apiClient,
	codexUrl,
	termsUrl,
	gdprUrl,
	defaultEmail,
	defaultName
}: IProps): React.ReactNode => {
	const [isDisabled, setIsDisabled] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
	// State to hold the selected row ID from the external button
	const [errors, setErrors] = useState<IErrors>({
		global: '',
		name: '',
		email: '',
		reason: ''
	});
	const [formValues, setFormValues] = useState<IValues>({
		interviewId: null,
		name: defaultName ?? '',
		email: defaultEmail ?? '',
		reason: ''
	});

	const { interviewId } = formValues;

	const onChange = useCallback(
		(e: any) => {
			const { name, value } = e.target;
			setFormValues({
				...formValues,
				[name]: value
			});
		},
		[setFormValues, formValues]
	);

	const sendForm = useCallback(
		(e: any) => {
			e.preventDefault();

			// basic validation
			let newErrors: IErrors = {
				global: '',
				name: !formValues.name ? 'Jméno nebo název firmy je povinné' : '',
				email: !formValues.email ? 'Email je povinný' : '',
				reason: !formValues.reason ? 'Důvod je povinný' : ''
			};

			// extra validation
			if (newErrors.email === '' && !validator.isEmail(formValues.email)) {
				newErrors.email = 'Email je ve špatném formátu';
			}

			if (newErrors.reason === '' && formValues.reason.length > 65535) {
				newErrors.reason = 'Text důvodu je příliš dlouhý';
			}

			// always refresh errors - empty after submit
			setErrors(newErrors);
			const noErrors = Object.values(newErrors).every((value) => value === '');

			if (noErrors) {
				setIsDisabled(true);

				const onSuccess = (response: any) => {
					if (response.result.success === true) {
						AppCookies.saveUserEmail(formValues.email);
						setIsSubmitted(true);
					}

					if (response.result.success === false) {
						for (const key of Object.keys(response.result.errors)) {
							const error = response.result.errors[key];

							if (error.type === 'email') {
								setErrors({
									...newErrors,
									email: 'Email je ve špatném formátu'
								});
								return;
							}
							if (error.type === 'duplicate') {
								setErrors({
									...newErrors,
									global: 'Tento pohovor jste již nahlásili'
								});
								return;
							}
						}
					}
				};

				const onCatch = (error: any) => {
					console.error(error);
					alert('Nastala chyba při odesílání formuláře, zkuste to prosím znovu.');
				};

				const onFinal = () => {
					setIsDisabled(false);
				};

				apiClient.addInterviewReport(formValues).then(onSuccess).catch(onCatch).finally(onFinal);
			}
		},
		[apiClient, formValues, setErrors, setIsSubmitted, setIsDisabled]
	);

	const close = useCallback(
		(e: any) => {
			e.preventDefault();
			modal.getInstance(document.getElementById('modalInterviewReportPopUp')!).hide();
		},
		[modal]
	);

	const addReportClick = useCallback(
		(event: { target: any }) => {
			const button = event.target;
			const id = parseInt(button.getAttribute('data-id'));

			if (interviewId !== id || isSubmitted) {
				setErrors({
					global: '',
					name: '',
					email: '',
					reason: ''
				});

				setFormValues({
					interviewId: id,
					name: defaultName ?? '',
					email: defaultEmail ?? '',
					reason: ''
				});
			}

			setIsSubmitted(false);
		},
		[interviewId, setErrors, setFormValues, setIsSubmitted, isSubmitted, defaultEmail, defaultName]
	);

	const createListeners = useCallback(
		(buttons: HTMLCollection) => {
			Array.from(buttons).forEach((button) => {
				button.addEventListener('click', addReportClick);
			});
		},
		[addReportClick]
	);

	const removeListeners = useCallback(
		(buttons: HTMLCollection) => {
			Array.from(buttons).forEach((button) => {
				button.removeEventListener('click', addReportClick);
			});
		},
		[addReportClick]
	);

	useEffect(() => {
		// refresh form when click on report button
		const buttons = document.getElementsByClassName('interview-add-report');
		createListeners(buttons);

		return () => {
			removeListeners(buttons);
		};
	}, [createListeners, removeListeners]);

	if (isSubmitted) {
		return (
			<div className="submitted">
				<div className="alert alert-success" role="alert">
					Děkujeme za nahlášení pohovoru. Váš požadavek bude zpracován.
				</div>
				<div className="mt-4 text-left">
					<button type="submit" className="btn btn-primary btn-medium" onClick={close}>
						Zavřít okno
					</button>
				</div>
			</div>
		);
	} else {
		return (
			<form onSubmit={sendForm}>
				{errors.global && <div className="alert alert-danger">{errors.global}</div>}
				<div className="form-row form-group required">
					<label className="h5 control-label" htmlFor={'interview-report-name'}>
						Vaše jméno nebo název firmy
					</label>
					<input
						id={'interview-report-name'}
						type="text"
						name="name"
						className={`form-control ${errors.name ? 'is-invalid' : ''}`}
						autoComplete="off"
						placeholder="Jméno nebo název firmy"
						value={formValues.name}
						onChange={onChange}
					/>
					{errors.name && <span className="invalid-feedback">{errors.name}</span>}
				</div>

				<div className="form-row form-group required mt-4">
					<label className="h5 control-label" htmlFor={'interview-report-email'}>
						Váš e-mail, na který vám zašleme výsledek posouzení pohovoru
					</label>
					<input
						id={'interview-report-email'}
						name="email"
						className={`form-control ${errors.email ? 'is-invalid' : ''}`}
						autoComplete="off"
						placeholder="E-mailová adresa"
						value={formValues.email}
						onChange={onChange}
					/>
					{errors.email && <span className="invalid-feedback">{errors.email}</span>}
				</div>

				<div className="form-row form-group required mt-4">
					<label className="h5 control-label" htmlFor={'interview-report-reason'}>
						Popište proč pohovor porušuje{' '}
						<a href={codexUrl} target={'_blank'} rel="noreferrer">
							Zásady férového hodnocení
						</a>
					</label>
					<textarea
						id={'interview-report-reason'}
						name="reason"
						className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
						autoComplete="off"
						value={formValues.reason}
						onChange={onChange}
					/>
					{errors.reason && <span className="invalid-feedback">{errors.reason}</span>}
				</div>

				<div className="mt-4 text-left">
					<button type="submit" className="btn btn-primary btn-medium" disabled={isDisabled}>
						{isDisabled ? 'Probíhá odesílaní' : 'Nahlásit pohovor'}
					</button>
				</div>

				<div className="mt-5 fs-tiny">
					Kliknutím na tlačítko Nahlásit pohovor souhlasíte se{' '}
					<a href={gdprUrl} target={'_blank'} rel="noreferrer">
						Zásady ochrany osobních údajů
					</a>
					{' a '}
					<a href={termsUrl} target={'_blank'} rel="noreferrer">
						Obchodními podmínkami
					</a>{' '}
					pro uživatele webu JenFirmy.cz a JenPráce.cz.
				</div>
			</form>
		);
	}
};
